import { useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { LOCATION_TIMEZONE_QUERY } from 'app/hooks/useLocationTimezone'
import { isQuietHours } from 'app/lib/smsQuietHours'

// One tick per minute keeps the visible gate honest while a conversation sits
// open across the 8:00 PM / 8:00 AM boundary; send paths still re-check at the
// moment of send, so the tick only drives what the user sees.
const TICK_MS = 60_000

/**
 * Whether SMS quiet hours are in effect right now at a location, kept live by
 * a minute tick. Also returns the location's IANA timezone so send paths can
 * re-check exactly at send time.
 *
 * `resolved` separates a still-loading timezone from a confirmed missing or
 * invalid one: while the query is in flight, `quiet` is computed off the
 * viewer's clock and may be wrong for the practice, so callers must not paint
 * a blocked state until `resolved` is true. The viewer-clock fallback then
 * applies only to locations that genuinely have no usable timezone. With no
 * locationId there is nothing to load, so that resolves immediately.
 */
export function useSmsQuietHours(locationId?: string | null): {
  quiet: boolean
  timezone?: string
  resolved: boolean
} {
  const [{ data, fetching }] = useQuery({
    query: LOCATION_TIMEZONE_QUERY,
    variables: { id: locationId! },
    pause: !locationId,
  })
  const timezone = data?.location?.timezone ?? undefined
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((tick) => tick + 1), TICK_MS)
    return () => clearInterval(id)
  }, [])
  return { quiet: isQuietHours(timezone), timezone, resolved: !locationId || !fetching }
}
